import config from "appConfig";
import { getLinkForCourseLesson } from "../../../../services/Utils";

import lesson0421 from "../lesson-21/lesson";
const linkLesson0421 = getLinkForCourseLesson("e0db4477-1995-4380-9c35-2338dcf5e504", lesson0421.id);

const theory = ` <h3 class=sectionHead>Introduction</h3>
<p>Enclosed and vented loudspeakers represent a large part of the market, but other systems [1] have also been developed to offer alternative solutions or to meet particular constraints (very small volumes, very low cut-off frequency, etc.). These different systems will be presented in this Lesson.
<h3 class=sectionHead>Passive radiator systems</h3>
<p>
   With the development of wireless audio transmission systems, the need for portable, and therefore small enclosure volume, loudspeakers has arisen. With small volumes of the order of one litre, solutions such as closed or vented enclosures are not possible if a low enough cut-off frequency is desired. Thus, tuning a
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mn>1</mn>
   </math>
    litre bass-reflex system at
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mn>80</mn>
   </math>
    Hz would require a vent of about
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mn>0</mn>
      <mo class=MathClass-punc>.</mo>
      <mn>9</mn>
   </math>
    m of length for a diameter of
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mn>5</mn>
   </math>
    cm, which is a rather bulky for a portable system. A possible solution is to replace the bass reflex system’s vent with a Passive Radiator (PR) [2, 3]. A passive radiator is essentially a membrane attached to a chassis by means of a suspension system (spider and surround), its behaviour can be approximated at low frequencies by a damped mechanical oscillator. In comparison to a normal speaker, the passive radiator has none of the motor parts (neither magnet nor polar pieces nor voice coil). It would then be possible to tune it to the desired frequency without taking up too much volume. This can be achieved by adjusting the mass and compliance of the passive radiator. This can be done be choosing a PR with the required mass and compliance. If necessary, the PR resonance frequency can be lowered by gluing rings with rated weight on the PR moving parts.<br class=newline>A sketch of a PR system is shown in <a href=# data-img-src=${config.ASSETS_URL}/04/22/theory/figure-1.svg data-img-alt="Figure 1. Passive radiator system.">Figure 1</a>. At low frequency, the PR of surface
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>S</mi>
         </mrow>
         <mrow>
            <mi>d</mi>
         </mrow>
      </msub>
   </math>
   can be modelled using a mechanical 1-DOF oscillator of mass
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>M</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>mp</mi>
         </mrow>
      </msub>
   </math>
   (with radiation terms included), compliance
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>C</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>mp</mi>
         </mrow>
      </msub>
   </math>
   and resistance
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>mp</mi>
         </mrow>
      </msub>
   </math>. Note that a second degree of freedom is added by the driver, giving to 2-DOF for the complete system.
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/04/22/theory/figure-1.svg alt="Figure 1. Passive radiator system."><figcaption class=caption>Figure 1. Passive radiator system.</figcaption></figure>
<p>
   The passive radiator system is quite similar to the vented system. To switch from one to the other, you only need to add two terms:
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>C</mi>
         </mrow>
         <mrow>
            <mi>p</mi>
         </mrow>
      </msub>
   </math>
   and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>p</mi>
         </mrow>
      </msub>
   </math>
   in the branch where the vent was and to replace the mass vent by
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>M</mi>
         </mrow>
         <mrow>
            <mi>p</mi>
         </mrow>
      </msub>
   </math>. It is then possible to obtain the electrical and acoustic circuits of the passive radiator system by modifying Figs.
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mn>2</mn>
   </math>
   and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mn>4</mn>
   </math>
   of <a href="${linkLesson0421}" target="_blank">Lesson 21</a> on bass reflex systems. The analogous electrical circuit of the passive radiator system is given in <a href=# data-img-src=${config.ASSETS_URL}/04/22/theory/figure-2.svg data-img-alt="Figure 2. Analogous electrical circuit of a PR system.">Figure 2</a> while the analogous acoustical circuit is given in <a href=# data-img-src=${config.ASSETS_URL}/04/22/theory/figure-3.svg data-img-alt="Figure 3. Analogous acoustical circuit of a PR system.">Figure 3</a>. Note that the right branch of <a href=# data-img-src=${config.ASSETS_URL}/04/22/theory/figure-3.svg data-img-alt="Figure 3. Analogous acoustical circuit of a PR system.">Figure 3</a> includes the passive radiator elements:
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>M</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ap</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>M</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>mp</mi>
         </mrow>
      </msub>
      <mo class=MathClass-bin>∕</mo>
      <msubsup>
         <mrow>
            <mi>S</mi>
         </mrow>
         <mrow>
            <mi>d</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msubsup>
   </math>,
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ap</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>mp</mi>
         </mrow>
      </msub>
      <mo class=MathClass-bin>∕</mo>
      <msubsup>
         <mrow>
            <mi>S</mi>
         </mrow>
         <mrow>
            <mi>d</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msubsup>
   </math>
   and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>C</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ap</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>C</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>mp</mi>
         </mrow>
      </msub>
      <mo class=MathClass-bin>×</mo>
      <msubsup>
         <mrow>
            <mi>S</mi>
         </mrow>
         <mrow>
            <mi>d</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msubsup>
   </math>. The elements characterising the passive radiator are also present in <a href=# data-img-src=${config.ASSETS_URL}/04/22/theory/figure-2.svg data-img-alt="Figure 2. Analogous electrical circuit of a PR system.">Figure 2</a>:
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>C</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ep</mi>
         </mrow>
      </msub>
   </math>,
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>M</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ep</mi>
         </mrow>
      </msub>
   </math>
   and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ep</mi>
         </mrow>
      </msub>
   </math>. Note that these elements are here in parallel, this is due to the electrodynamic coupling that links voltages to currents. Their literal expressions are given by
<table class=equation>
   <tr>
      <td style="font-size: 8px">
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>C</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>ep</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>M</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>mp</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>∕</mo>
            <mi>B</mi>
            <msup>
               <mrow>
                  <mi>ℓ</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
            <mstyle class=mbox>
               <mtext>, </mtext>
            </mstyle>
            <msub>
               <mrow>
                  <mi>M</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>ep</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>C</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>mp</mi>
               </mrow>
            </msub>
            <mi>B</mi>
            <msup>
               <mrow>
                  <mi>ℓ</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
            <mstyle class=mbox>
               <mtext> and </mtext>
            </mstyle>
            <msub>
               <mrow>
                  <mi>R</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>ep</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>R</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>mp</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>∕</mo>
            <mi>B</mi>
            <msup>
               <mrow>
                  <mi>ℓ</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
         </math>
      <td class=eq-no>(1)
</table>
<figure class=figure>
   <p>
      <img src=${config.ASSETS_URL}/04/22/theory/figure-2.svg alt="Figure 2. Analogous electrical circuit of a PR system.">
      <figcaption class=caption>Figure 2. Analogous electrical circuit of a PR system.</figcaption>
</figure>
<figure class=figure><p><img src=${config.ASSETS_URL}/04/22/theory/figure-3.svg alt="Figure 3. Analogous acoustical circuit of a PR system."><figcaption class=caption>Figure 3. Analogous acoustical circuit of a PR system.</figcaption></figure>
<p>
   By using the circuit shown in <a href=# data-img-src=${config.ASSETS_URL}/04/22/theory/figure-2.svg data-img-alt="Figure 2. Analogous electrical circuit of a PR system.">Figure 2</a>, the electrical impedance
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>epr</mi>
         </mrow>
      </msub>
   </math>
   of the PR system is calculated and plotted in <a href=# data-img-src=${config.ASSETS_URL}/04/22/theory/figure-4.svg data-img-alt="Figure 4. Electrical impedance of a PR system.">Figure 4</a>. The response is computed for
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>V</mi>
         </mrow>
         <mrow>
            <mi>b</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>V</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>as</mi>
         </mrow>
      </msub>
      <mo class=MathClass-bin>∕</mo>
      <mn>1</mn>
      <mo class=MathClass-punc>.</mo>
      <mn>3</mn>
   </math>,
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>C</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>mp</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>C</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ms</mi>
         </mrow>
      </msub>
   </math>
   and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>mp</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ms</mi>
         </mrow>
      </msub>
   </math>. The resonance frequency of the passive radiator
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi>p</mi>
         </mrow>
      </msub>
   </math>
   is tuned at
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi>s</mi>
         </mrow>
      </msub>
      <mo class=MathClass-bin>∕</mo>
      <mn>1</mn>
      <mo class=MathClass-punc>.</mo>
      <mn>5</mn>
   </math>. Note the two bumps on the curve that are consistent with a system with two degrees of freedom.
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/04/22/theory/figure-4.svg alt="Figure 4. Electrical impedance of a PR system."><figcaption class=caption>Figure 4. Electrical impedance of a PR system.</figcaption></figure>
<p>
   The on-axis response of the system whose electric impedance is plotted in <a href=# data-img-src=${config.ASSETS_URL}/04/22/theory/figure-2.svg data-img-alt="Figure 2. Analogous electrical circuit of a PR system.">Figure 2</a> is represented in <a href=# data-img-src=${config.ASSETS_URL}/04/22/theory/figure-5.svg data-img-alt="Figure 5. On-axis response of a PR system for <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mn>1</mn></math> W.">Figure 5</a>. It can be seen that the passive radiator, tuned at
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi>s</mi>
         </mrow>
      </msub>
      <mo class=MathClass-bin>∕</mo>
      <mn>1</mn>
      <mo class=MathClass-punc>.</mo>
      <mn>5</mn>
   </math>, increases the radiated pressure level below
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi>s</mi>
         </mrow>
      </msub>
   </math>. At very low frequencies, the passive radiator can be considered as rigid, and we have the behaviour of a closed-box speaker (with a slope in
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msup>
         <mrow>
            <mi>ω</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msup>
   </math>). In the frequency range, where the passive radiator plays a role, we obtain a behaviour similar to the vented speaker (with a slope in
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msup>
         <mrow>
            <mi>ω</mi>
         </mrow>
         <mrow>
            <mn>4</mn>
         </mrow>
      </msup>
   </math>).
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/04/22/theory/figure-5.svg alt="Figure 5. On-axis response of a PR system for <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mn>1</mn></math> W."><figcaption class=caption>Figure 5. On-axis response of a PR system for <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mn>1</mn></math> W.</figcaption></figure>
<p>PR systems almost behave as vented-box systems when properly tuned. Compared to vented systems, they have the following advantages:
<ul class=itemize1>
   <li class=itemize>They can be used for small volume enclosures whereas this is often not possible with ported loudspeakers.
   <li class=itemize>No vent noise.
   <li class=itemize>Stronger isolation of the internal standing waves.
</ul>
<p>However, they are generally more difficult to adjust, especially if the suspension compliance has a low tolerance.
<h3 class=sectionHead>Transmission line Speakers</h3>
<p>A transmission line system is an enclosure design that uses a long waveguide at the back of the loudspeaker. To save space, this waveguide is usually folded (see <a href=# data-img-src=${config.ASSETS_URL}/04/22/theory/figure-6.svg data-img-alt="Figure 6. Transmission line speaker system.">Figure 6</a>). The transmission line can be:
<ul class=itemize1>
   <li class=itemize>closed at the end, in this case the pathway will be filled with damping material to absorb the energy radiated from the back of the membrane and eliminate the effect of waveguide resonant frequencies,
   <li class=itemize>open at the end, it will bring the wave emitted from the rear of the membrane back into phase with that emitted from the front. Absorbing material is also used to attenuate energy above the tuning frequency.
</ul>
<p>
   Transmission line loudspeakers are generally more difficult to design, especially the open versions. Indeed, the position of the loudspeaker and the amount of absorbing material must be particularly well chosen in order to bring the back wave in phase in the desired frequency band while attenuating the higher frequencies to avoid comb filtering effects.
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/04/22/theory/figure-6.svg alt="Figure 6. Transmission line speaker system."><figcaption class=caption>Figure 6. Transmission line speaker system.</figcaption></figure>
<h3 class=sectionHead>Subwoofers</h3>
<p>
   A subwoofer, often shortened to “sub”, is a system that is dedicated to the reproduction of low frequencies. Typically, below
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mn>200</mn>
   </math>
    Hz for consumer products,
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mn>100</mn>
   </math>
    Hz for pro audio systems and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mn>80</mn>
   </math>
    Hz for movie theatres. Conventional designs such as closed box, vented box or passive radiator systems are usually chosen. Other technical solutions such as horns or transmission lines can also be used. More specific designs have also been created like the isobaric loading shown in <a href=# data-img-src=${config.ASSETS_URL}/04/22/theory/figure-7.svg data-img-alt="Figure 7. Isobaric and bandpass systems.">Figure 7</a> left or the band-pass system which is acoustically filtered (see <a href=# data-img-src=${config.ASSETS_URL}/04/22/theory/figure-7.svg data-img-alt="Figure 7. Isobaric and bandpass systems.">Figure 7</a> right). For an isobaric system, two identical loudspeakers are used, the aim being for them to vibrate with the same displacement in order to maintain a constant volume between their two diaphragms. However, this is difficult to achieve because the two loudspeakers are loaded by different acoustic circuits and are generally not perfectly identical.  However, in well-tuned systems, the two loudspeakers behave much like a single loudspeaker in a cabinet volume that is about twice as large. In the case of band-pass design, the system has a band-pass response with a rolloff of 
    <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msup>
         <mrow>
            <mi>ω</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msup>
   </math>
on each side. The central frequency and the bandwidth can be tuned by appropriately designing the rear enclosure, the front enclosure and the Helmholtz frequency 
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi>b</mi>
         </mrow>
      </msub>
   </math>. In any case, a compromise has to be found between low cut-off frequency, volume and system efficiency.<br class=newline>
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/04/22/theory/figure-7.svg alt="Figure 7. Isobaric and bandpass systems."><figcaption class=caption>Figure 7. Isobaric and bandpass systems.</figcaption></figure>
<p>Active subwoofers include a built-in amplifier which is often associated to other features. For instance, a tunable low pass-filter is generally implemented to adjust the crossover frequency with the main system. For some subwoofers, a tunable phase setting can be added to avoid destructive interferences with other subwoofers or the main system. Active equalisation which uses a level dependant filter can be also used to reduce non linearities. Another solution is to add a feedback mechanism that uses the signal from an accelerometer glued to the membrane to reduce distortion.
<h3 class=sectionHead>Portable speakers</h3>
<p>Wireless speakers have become very popular since the 2010s. These systems incorporate, in addition to the speaker(s), amplifier(s) and battery, a transmission system, which allows communication with different audio devices such as laptops, phones, tablets, etc. In order to simplify their portability, they generally have a volume that does not exceed one litre. In order to have a sufficiently low cut-off frequency, some models use a passive radiator.<br class=newline>Smart speakers appeared at the end of the 2010s. These include a voice user interface and a virtual assistant that allows, by voice commands, to perform different tasks (increase the volume, play a specific artist, etc.).
<h3 class=sectionHead>Conclusion</h3>
<p>In this Lesson, alternative solutions to closed and bass-reflex speakers were presented. This section is not intended to be exhaustive, as the creativity of electroacoustic engineers has led to the development of many alternative solutions. However, the systems presented in this and previous lessons represent a large part of the solutions available on the market.
<h3 class=likesectionHead>Glossary</h3>
<dl>
  <dt>1-DOF
  <dd>1 Degree Of Freedom
  <dt>2-DOF
  <dd>2 Degrees Of Freedom
  <dt>PR
  <dd>Passive Radiator
</dl>
<h3 class=sectionHead>References</h3>
<dl class=thebibliography>
   <dt class=thebibliography id=X0-colloms2018high>[1]
   <dd class=thebibliography id=bib-1>
      <p>Martin Colloms. <span class=cmti-10>High performance loudspeakers: optimising high fidelity </span><span class=cmti-10>loudspeaker systems</span>. John Wiley & Sons, 2018. <span class=cmcsc-10><span class=small-caps>isbn</span></span>: 978111841353-1.
   <dt class=thebibliography id=X0-small1974passivea>[2]
   <dd class=thebibliography id=bib-2>
      <p>Richard H Small. “Passive-radiator loudspeaker systems Part 1: Analysis”. In: <span class=cmti-10>Journal of the Audio Engineering Society </span>22.8 (1974), pp. 592–601.
   <dt class=thebibliography id=X0-small1974passiveb>[3]
   <dd class=thebibliography id=bib-3>
      <p>Richard M Small. “Passive-Radiator Loudspeaker Systems, Part 2: Synthesis”. In: <span class=cmti-10>Journal of the Audio Engineering Society </span>22.9 (1974), pp. 683–689.
</dl>`;

export default theory;
